import { uploadMimeType } from './mediaPolicy';
// Valuebell API client
// Handles both Drive URL and Direct Upload (resumable GCS upload) flows

const API_BASE_URL =
  process.env.REACT_APP_VALUEBELL_API_BASE_URL ||
  'https://valuebell-post-production-express-back-115346807311.me-west1.run.app';

const CHUNK_SIZE = 8 * 1024 * 1024; // 8 MB chunks for resumable upload

async function getAuthHeader(user) {
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

// Step 1: Ask backend for a resumable upload URL
export async function requestUploadUrl(user, file) {
  const contentType = uploadMimeType(file);
  const headers = await getAuthHeader(user);
  const res = await fetch(`${API_BASE_URL}/upload/request-url`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      fileName: file.name,
      fileSize: file.size,
      contentType,
    }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(apiError(text, 'Could not prepare the upload. Please try again.'));
  }
  return res.json(); // { uploadUrl, gcsPath, uploadId }
}

// Step 2a: Initiate the resumable upload session via POST to the signed URL.
// Returns the session URI from the Location header.
async function initiateResumableUpload(signedUrl, contentType) {
  const response = await fetch(signedUrl, {
    method: 'POST',
    headers: {
      'Content-Type': contentType,
      'x-goog-resumable': 'start',
    },
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Failed to initialize upload: ${response.status} ${errorText}`);
  }

  const sessionUri = response.headers.get('location');
  if (!sessionUri) {
    throw new Error('No session URI returned from GCS');
  }

  return sessionUri;
}

// Step 2b: Upload a single chunk to the session URI.
function uploadChunk(sessionUri, chunk, start, end, total, contentType) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', sessionUri, true);
    xhr.setRequestHeader('Content-Range', `bytes ${start}-${end - 1}/${total}`);
    if (contentType) xhr.setRequestHeader('Content-Type', contentType);
    xhr.onload = () => {
      // 200/201 = complete, 308 = resume incomplete (intermediate chunk OK)
      if (xhr.status === 200 || xhr.status === 201 || xhr.status === 308) {
        resolve(xhr.status);
      } else {
        reject(new Error(`Upload failed at ${start}: HTTP ${xhr.status} ${xhr.statusText}`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(chunk);
  });
}

export async function uploadFileResumable(uploadUrl, file, onProgress) {
  const total = file.size;
  const contentType = uploadMimeType(file);

  // Initiate the resumable session to get the session URI
  const sessionUri = await initiateResumableUpload(uploadUrl, contentType);

  let offset = 0;
  while (offset < total) {
    const end = Math.min(offset + CHUNK_SIZE, total);
    const chunk = file.slice(offset, end);
    // eslint-disable-next-line no-await-in-loop
    await uploadChunk(sessionUri, chunk, offset, end, total, contentType);
    offset = end;
    if (onProgress) onProgress(Math.round((offset / total) * 100));
  }
}

// Step 3: Trigger the Valuebell job (works for both modes)
export async function triggerJob(user, payload) {
  const headers = await getAuthHeader(user);
  const res = await fetch(`${API_BASE_URL}/valuebell-mapper/trigger`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(apiError(text, 'Could not start processing. Please try again.'));
  }
  return res.json(); // { jobId }
}

// Check if a Google Drive file is publicly accessible
export async function checkDriveAccess(user, fileId) {
  const headers = await getAuthHeader(user);
  const res = await fetch(`${API_BASE_URL}/drive/check-access`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileId }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Drive access check failed (${res.status}): ${text}`);
  }
  return res.json(); // { accessible: boolean }
}

// Generate a share link for an episode
export async function generateShareLink(user, runId) {
  const headers = await getAuthHeader(user);
  const res = await fetch(`${API_BASE_URL}/share/generate`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ runId }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Share failed (${res.status}): ${text}`);
  }
  return res.json(); // { shareToken }
}

// Fetch a publicly shared episode by token (no auth required)
export async function getSharedEpisode(token) {
  const res = await fetch(`${API_BASE_URL}/share/${encodeURIComponent(token)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to load shared episode');
  return res.json();
}

// Ask the API for a short-lived attachment URL with the short's title as filename.
export async function getShortDownloadLink(user, runId, shortId) {
  const headers = await getAuthHeader(user);
  const response = await fetch(`${API_BASE_URL}/shorts/download`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ runId, shortId: String(shortId) }),
  });
  if (!response.ok) throw new Error('Could not prepare the download. Please try again.');
  return response.json();
}

export async function getSharedShortDownloadLink(token, shortId) {
  const response = await fetch(`${API_BASE_URL}/share/${encodeURIComponent(token)}/shorts/${encodeURIComponent(shortId)}/download`);
  if (!response.ok) throw new Error('Could not prepare the download. Please try again.');
  return response.json();
}

// Create shorts for an existing run
export async function createShorts(user, runId) {
  const headers = await getAuthHeader(user);
  const res = await fetch(`${API_BASE_URL}/shorts/create`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ runId }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(apiError(text, 'Could not create shorts. Please try again.'));
  }
  return res.json();
}

export async function createMapping(user, runId) {
  const headers = await getAuthHeader(user);
  const res = await fetch(`${API_BASE_URL}/mapping/create`, {
    method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ runId }),
  });
  if (!res.ok) throw new Error(`Could not create show notes: ${await res.text()}`);
  return res.json();
}

function apiError(text, fallback) {
  try {
    const { message } = JSON.parse(text);
    return typeof message === 'string' ? message : Array.isArray(message) ? message.join(' ') : fallback;
  } catch { return fallback; }
}
